/* eslint-disable react/jsx-max-depth */
import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import EditIcon from '@mui/icons-material/Edit';
import FingerprintIcon from '@mui/icons-material/Fingerprint';

import { ensureDefined } from '@e-socna/utils';
import {
  drillFragmentData,
  getFragmentData,
  graphql,
} from '@e-socna/data-layer';
import { EMPTY_ARRAY } from '@e-socna/empty';
import type { FolderWorkflowId } from '@e-socna/data-layer';

import { useFolderAccess } from '../../components/Access/hooks/useFolderAccess.hook';
import { Memo } from '../../components/Memo';
import { RightDrawer } from '../../components/RightDrawer';
import { FolderForm } from '../../forms/Folder';
import { FormVariant } from '../../forms';
import { FolderFormTab } from '../../forms/Folder/enums/tab.enum';
import { SpeedDial } from '../../components/SpeedDial';
import { Code } from '../../components/Code';
import { Avatar } from '../../components/Avatar';
import { ThirdPartyName } from '../../components/ThirdPartyName';
import { formatDateTime } from '../../utils/format-date-time.util';
import { useReferencesContext } from '../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { useFolderReadyContext } from '../../contexts/FolderContext/hooks/useFolderReadyContext.hook';
import { FolderStateForm } from '../../forms/FolderState';

import { FolderPage_Tabs_Folder_Fragment } from './Tabs';

export const FolderPage_GeneralInfo_Folder_Fragment = graphql(`
  fragment FolderPage_GeneralInfo_Folder_Fragment on Folder {
    _id
    codingConfig {
      kind
      strategyId
      ... on CodingConfigManual {
        code
      }
      ...CodeCmp_CodingConfig_Fragment
    }
    code
    businessCreationDate
    businessCreatorId
    color
    stateId
    workflowId
    isConfidential
    internalMemo
    memo

    consolidation {
      title
      description
    }

    ...FolderForm_Folder_Fragment
  }
`);

interface Props {
  readonly paths: Readonly<{
    consult: string;
    editGeneralInfo: string;
    editCode: string;
    editState: string;
  }>;
}

export const GeneralInfo = ({ paths }: Readonly<Props>) => {
  const { folder: rawFolder } = useFolderReadyContext();
  const tabsFolder = getFragmentData(
    FolderPage_Tabs_Folder_Fragment,
    rawFolder,
  );
  const folder = drillFragmentData(
    [FolderPage_Tabs_Folder_Fragment, FolderPage_GeneralInfo_Folder_Fragment],
    rawFolder,
  );

  const { folderWorkflowsIdMap } = useReferencesContext();

  const { pathname: currentPath } = useLocation();
  const navigate = useNavigate();

  const isInGeneralInfoEditionMode = currentPath === paths.editGeneralInfo;
  const isInCodeEditionMode = currentPath === paths.editCode;
  const isInStateEditionMode = currentPath === paths.editState;
  const isInEditionMode = isInGeneralInfoEditionMode || isInCodeEditionMode;
  const leaveEditionMode = useCallback(
    () => void navigate(paths.consult),
    [navigate, paths.consult],
  );

  const enterStateEditionMode = useCallback(
    () => void navigate(paths.editState),
    [navigate, paths.editState],
  );

  const { folderWorkflow } = ensureDefined({
    folderWorkflow: folderWorkflowsIdMap[folder.workflowId as FolderWorkflowId],
  });

  const access = useFolderAccess(folder._id, folder);

  return (
    <>
      <Stack spacing={2} sx={{ pt: 4, width: '100%' }}>
        <FolderStateForm
          enterEditionMode={enterStateEditionMode}
          folder={tabsFolder}
          isInEditionMode={isInStateEditionMode}
          leaveEditionMode={leaveEditionMode}
        />
        <Paper sx={{ p: 2 }}>
          <Box sx={{ position: 'relative' }}>
            <Typography gutterBottom variant="h6">
              {folder.consolidation.title}
            </Typography>
            <Typography
              gutterBottom
              sx={{ whiteSpace: 'pre-line' }}
              variant="body1"
            >
              {folder.consolidation.description
                ? folder.consolidation.description
                : 'Aucune description renseignée'}
            </Typography>
            <Avatar
              bgColor={folder.color}
              icon={<FolderOpenIcon />}
              label="Avatar du Dossier"
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
              }}
            />
          </Box>
        </Paper>
        <Memo
          content={{
            internal: folder.internalMemo,
            memo: folder.memo,
          }}
          display={{ internal: true, memo: true }}
        />
        <Paper sx={{ p: 2 }}>
          <Box sx={{ position: 'relative' }}>
            <Code code={folder.code} codingConfig={folder.codingConfig} />
            <Typography gutterBottom variant="body1">
              Date de création : {formatDateTime(folder.businessCreationDate)}
            </Typography>
            <Typography gutterBottom variant="body1">
              Créateur : <ThirdPartyName id={folder.businessCreatorId} />
            </Typography>
            <Typography gutterBottom variant="body1">
              Workflow : {folderWorkflow.label}
            </Typography>
            <Typography gutterBottom variant="body1">
              Confidentiel : {folder.isConfidential ? 'Oui' : 'Non'}
            </Typography>
          </Box>
        </Paper>
      </Stack>
      <SpeedDial
        actions={[
          {
            id: 'editFolderGeneralInfo',
            gate: access.update,
            icon: <EditIcon />,
            name: 'Modifier Infos Générales',
            to: paths.editGeneralInfo,
          },
          {
            id: 'editFolderCode',
            gate: access.update,
            icon: <FingerprintIcon />,
            name: 'Modifier Code',
            to: paths.editCode,
          },
        ]}
        title="Actions possibles sur les Informations Générales du Dossier"
      />
      <RightDrawer isOpen={isInEditionMode} onDrawerClose={leaveEditionMode}>
        <FolderForm
          folderId={folder._id}
          formVariant={FormVariant.Modification}
          initialValues={folder}
          onCancel={leaveEditionMode}
          onSuccess={leaveEditionMode}
          stepIdsSubset={
            isInGeneralInfoEditionMode
              ? [FolderFormTab.GeneralInfo]
              : isInCodeEditionMode
                ? [FolderFormTab.CodingConfig]
                : EMPTY_ARRAY
          }
          title={
            isInGeneralInfoEditionMode
              ? 'Modifier les Informations Générales du Dossier'
              : isInCodeEditionMode
                ? 'Modifier le Code du Dossier'
                : 'Modifier le Dossier'
          }
        />
      </RightDrawer>
    </>
  );
};
